import { useTranslation } from "next-i18next";
import { useState } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import { SlideBulletNavigation } from "../../../../../components/blog/RelatedPosts/SlideBulletNavigation";
import { device, size } from "../../../../../constants";
import { cn } from "../../../../../lib/utils";
import { Line } from "../../../casos/components/Line";
import { Subtitle } from "../../../casos/components/Subtitle";
import "swiper/css";

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  .bullets {
    .bullet {
      background-color: ${({ theme }) => theme.colors["vivid-03"]};

      :is([data-theme="dark"] &) {
        background-color: ${({ theme }) => theme.colors["vivid-03"]};
      }
    }
  }

  @media ${device.laptop} {
    .arrows {
      display: block;
      margin-left: auto;
    }
  }
`;

const SwiperWrapper = styled.div`
  .bullets-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bullet {
    background-color: ${({ theme }) => theme.colors["vivid-03"]};
    opacity: 0.5;
  }

  .bullet-active {
    opacity: 1;
  }

  :is([data-theme="dark"] &) {
    .bullet {
      background-color: ${({ theme }) => theme.colors["vivid-03"]};
    }
  }

  .swiper-slide:last-child > div {
    border-right: none;
  }
`;

function PainPointCard({ title, description, position }) {
  return (
    <div className="laptop:h-[400px] h-[380px] flex items-end">
      <div
        className={cn(
          "shadow-6 rounded-lg bg-neutral-07 relative",
          "laptop:w-[400px] laptop:h-[352px] laptop:p-8 laptop:pt-0 laptop:mx-0 laptop:mr-auto",
          "p-6 h-[340px] w-[342px] mx-auto"
        )}
      >
        <div
          className={cn(
            "shadow-3 bg-neutral-06 absolute rounded-lg mx-auto top-0 -translate-y-1/2 left-1/2 -translate-x-1/2",
            "flex items-center justify-center",
            "laptop:w-[132px] laptop:h-[96px] w-[96px] h-[72px]"
          )}
        >
          <span
            className={cn(
              "font-medium text-[48px] leading-[48px] text-vivid-03",
              "laptop:text-[56px]"
            )}
          >
            {position}
          </span>
        </div>
        <h3
          className={cn(
            "text-center text-white font-medium text-xl leading-[32px] mb-2 mt-[48px]",
            "laptop:text-2xl laptop:mt-[80px] laptop:mb-4"
          )}
        >
          {title}
        </h3>
        <p
          className={cn(
            "text-center text-neutral-03 text-lg leading-[30px]",
            "laptop:text-xl laptop:leading-8"
          )}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

export default function PainPointsSection() {
  const { t } = useTranslation();
  const [activeBullet, setActiveBullet] = useState(0);

  const painPoints = [
    {
      position: "01",
      title: t("products.squad-ia.painPoints.items.backlog.title"),
      description: t("products.squad-ia.painPoints.items.backlog.description"),
    },
    {
      position: "02",
      title: t("products.squad-ia.painPoints.items.cost.title"),
      description: t("products.squad-ia.painPoints.items.cost.description"),
    },
    {
      position: "03",
      title: t("products.squad-ia.painPoints.items.dependency.title"),
      description: t("products.squad-ia.painPoints.items.dependency.description"),
    },
    {
      position: "04",
      title: t("products.squad-ia.painPoints.items.quality.title"),
      description: t("products.squad-ia.painPoints.items.quality.description"),
    },
  ];

  return (
    <section className="bg-neutral-07">
      <div className="container-blog">
        <div className="flex flex-col items-center mb-10 laptop:mb-14">
          <Line className="border-vivid-03" />

          <Subtitle className="max-w-[688px] text-center font-medium">
            <h2>{t("products.squad-ia.painPoints.title")}</h2>
          </Subtitle>
        </div>

        <SwiperWrapper>
          <Swiper
            slidesPerView={1}
            navigation={false}
            spaceBetween={24}
            breakpoints={{
              [size.tablet]: {
                slidesPerView: 2,
              },
              [size.laptop]: {
                slidesPerView: 2,
              },
              [size.laptopL]: {
                slidesPerView: 4,
              },
            }}
            onSlideChange={(e) => {
              setActiveBullet(e.activeIndex);
            }}
          >
            {painPoints.map((point) => (
              <SwiperSlide key={point.position}>
                <PainPointCard {...point} />
              </SwiperSlide>
            ))}

            <NavigationContainer className="mt-6">
              <div className="bullets">
                <SlideBulletNavigation activeBullet={activeBullet} />
              </div>
            </NavigationContainer>
          </Swiper>
        </SwiperWrapper>
      </div>
    </section>
  );
}
